import {
  faCheck,
  faExclamationTriangle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';

import { NEUTRAL_TIER_COLOR, TIER_COLORS, SOURCE_LABELS } from '../constants';

import Alert from '@/components/Alert';
import Avatar from '@/components/Avatar';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import ScoreRing from '@/routes/Agency/WorkerV2/components/dependabilityV2/ScoreRing';
import {
  formatRate,
  formatRating,
} from '@/routes/Agency/WorkerV2/components/dependabilityV2/StatCards';
import {
  DependabilityV2,
  dependabilityV2API,
  isDependabilityV2,
} from '@/services/dependabilityV2API';
import {
  PoolNominationRow,
  isClientReview,
} from '@/services/preferredPoolsAPI';
import styled from '@/styles';
import { formatISO } from '@/util/date';
import { trackPoolNominationReviewed } from '@/util/vexo';

// Per-source review modal (plan F7 / §5): a client nomination shows the exact
// rated shift; a system suggestion shows the qualifying criteria frozen at
// trigger time — the two shapes are intentionally different. Visual language
// from the prototype's #reviewModalOverlay (rv* ids kept as testids where the
// element ports).
//
// GRAVY-10363: the worker-profile summary above the review facts. Tax type,
// preferred-pools count and the skills breakdown ride the nominations row;
// the score ring, the three tiles and the no-show / late chips are read from
// the worker dependability endpoint on open so they match the Worker
// profile's Dependability card exactly (plan A-2). When that endpoint is
// unavailable (flag off → 404, or an error) the block degrades to "—" (A-6).

const Profile = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  textAlign: 'center',
  padding: '8px 0',
});

const Badges = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const TierBadge = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  borderRadius: 8,
  padding: '3px 8px',
  fontSize: '12px',
  fontWeight: 700,
  color: '#fff',
});

// Prototype .rv-tax-pill
const TaxPill = styled('span', {
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: '3px 10px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#1C1C1C',
});

// Prototype .rv-flags / .rv-flag (good | warn); "muted" is the "—" state when
// the dependability endpoint is unavailable or the count is unknown.
const Flags = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
});

const Flag = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  borderRadius: 100,
  padding: '4px 12px',
  fontSize: '12px',
  fontWeight: 600,
  border: '1px solid',
  whiteSpace: 'nowrap',
  variants: {
    kind: {
      good: { color: '#2E8722', borderColor: '#A7DAA1', background: '#fff' },
      warn: { color: '#BB8700', borderColor: '#F5D98A', background: '#FFF9EC' },
      muted: { color: '#5B5B5B', borderColor: '#DADADA', background: '#fff' },
    },
  },
});

// Prototype .rv-metrics / .rv-score-ring / .rv-metric-box
const Metrics = styled('div', {
  display: 'flex',
  alignItems: 'stretch',
  gap: 12,
  flexWrap: 'wrap',
});

const RingWrap = styled('div', {
  width: 70,
  height: 70,
  flexShrink: 0,
  alignSelf: 'center',
});

const MetricBox = styled('div', {
  flex: '1 1 110px',
  border: '1px solid #EAEAEA',
  borderRadius: 8,
  padding: '12px 16px',
});

const MetricLabel = styled('div', { fontSize: '14px', color: '#5B5B5B' });

const MetricValue = styled('div', {
  fontSize: '18px',
  fontWeight: 700,
  color: '#1C1C1C',
  marginTop: 4,
});

// Prototype .rv-skills-wrap / .rv-skill / .rv-skill-icon-wrap / .rv-skill-count
const SkillsTitle = styled('div', {
  fontSize: '18px',
  fontWeight: 700,
  color: '#1C1C1C',
  marginBottom: 12,
});

const SkillsList = styled('div', {
  display: 'flex',
  gap: 24,
  flexWrap: 'wrap',
});

const SkillItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  width: 70,
});

const SkillIconWrap = styled('div', {
  position: 'relative',
  width: 60,
  height: 60,
  borderRadius: '50%',
  border: '1.5px solid #44A735',
  background: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  '& img': { width: 44, height: 44, objectFit: 'contain' },
});

const SkillCount = styled('span', {
  position: 'absolute',
  bottom: 0,
  right: -6,
  background: '#44A735',
  color: '#fff',
  fontSize: '12px',
  fontWeight: 600,
  border: '1.5px solid #fff',
  borderRadius: 100,
  padding: '1px 7px',
  whiteSpace: 'nowrap',
});

const SkillName = styled('span', {
  fontSize: '12px',
  color: '#1C1C1C',
  textAlign: 'center',
});

const FactRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 16,
  padding: '10px 0',
  borderBottom: '1px solid #EAEAEA',
  '&:last-child': { borderBottom: 'none' },
  fontSize: '14px',
});

const FactLabel = styled('span', { color: '#5B5B5B' });
const FactValue = styled('span', { fontWeight: 600, color: '#1C1C1C' });

const tierLabel = (tier: string | null): string =>
  tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : '—';

const formatDate = (iso: string | null): string =>
  iso ? formatISO(iso, "EEE, MMM do yyyy 'at' h:mmbbb") : '—';

const TAX_LABELS: Record<string, string> = { '1099': '1099', w2: 'W2' };

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

const countChip = (
  count: number | null | undefined,
  noun: string,
  warnWhenPositive: boolean,
): { label: string; kind: 'good' | 'warn' | 'muted' } => {
  if (count == null) return { label: `— ${noun}s`, kind: 'muted' };
  if (warnWhenPositive) {
    return { label: plural(count, noun), kind: count > 0 ? 'warn' : 'good' };
  }
  return { label: plural(count, noun), kind: count > 0 ? 'good' : 'muted' };
};

const useWorkerDependability = (workerId: number): DependabilityV2 | null => {
  const [data, setData] = useState<DependabilityV2 | null>(null);

  useEffect(() => {
    let active = true;
    setData(null);
    dependabilityV2API
      .getDependability(workerId)
      .then((response) => {
        if (active && isDependabilityV2(response?.data)) setData(response.data);
      })
      .catch(() => {
        // 404 when the dependability flag is off, or a transient failure —
        // the summary degrades to "—" rather than blocking the review.
      });
    return () => {
      active = false;
    };
  }, [workerId]);

  return data;
};

type Props = {
  nomination: PoolNominationRow;
  skillName: string;
  deciding: boolean;
  // A failed decision keeps the modal open, so the failure has to be shown
  // here — the card-level alert renders behind the overlay (GRAVY-10339).
  error?: string | null;
  onApprove: (nomination: PoolNominationRow) => void;
  onDecline: (nomination: PoolNominationRow) => void;
  onClose: () => void;
};

const ReviewNominationModal = ({
  nomination,
  skillName,
  deciding,
  error,
  onApprove,
  onDecline,
  onClose,
}: Props) => {
  const { review, source } = nomination;
  const dependability = useWorkerDependability(nomination.worker.id);
  const taxLabel = nomination.tax_type
    ? (TAX_LABELS[nomination.tax_type] ?? nomination.tax_type)
    : null;
  const skills = nomination.skills ?? [];
  const noShow = countChip(
    dependability?.badges.no_shows_count,
    'no-show',
    true,
  );
  const pools = countChip(
    nomination.preferred_pools_count,
    'preferred pool',
    false,
  );
  const late = countChip(
    dependability?.badges.late_count,
    'late arrival',
    true,
  );
  const onTime = dependability?.stats.on_time_rate.current;
  const rating = dependability?.stats.client_rating.current;

  // pool_nomination_reviewed fires once per modal mount (2026-09-02 plan).
  useEffect(() => {
    trackPoolNominationReviewed({
      nomination_id: nomination.nomination_id,
      source: nomination.source,
      stale: !!nomination.stale,
      also_qualified: !!nomination.also_qualified,
      pending_age_s: nomination.occurred_at
        ? Math.round((Date.now() - Date.parse(nomination.occurred_at)) / 1000)
        : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      disableClickout
      size="sm"
      title="Review nomination"
      onRequestClose={onClose}
    >
      <Card.Section>
        <Profile>
          <Avatar
            firstName={nomination.worker.name ?? ''}
            size="lg"
            src={nomination.worker.avatar_url ?? undefined}
          />
          <div data-testid="rvName" style={{ fontSize: 18, fontWeight: 700 }}>
            {nomination.worker.name}
          </div>
          <Badges>
            <TierBadge
              data-testid="rvTierBadge"
              style={{
                background: nomination.tier
                  ? TIER_COLORS[nomination.tier]
                  : NEUTRAL_TIER_COLOR,
              }}
            >
              {tierLabel(nomination.tier)}
            </TierBadge>
            {taxLabel && <TaxPill data-testid="rvTaxPill">{taxLabel}</TaxPill>}
          </Badges>
          <Body>
            <b>{nomination.shifts_in_skill_total}</b> {skillName.toLowerCase()}{' '}
            shifts &middot; <b>{nomination.shifts_with_client}</b> shifts with
            client
          </Body>
          <Small>{SOURCE_LABELS[source] ?? source}</Small>
        </Profile>
      </Card.Section>
      <Card.Section>
        <Stack vertical gap={16}>
          <Flags data-testid="rvFlags">
            <Flag data-testid="rvNoShowChip" kind={noShow.kind}>
              {noShow.label}
            </Flag>
            <Flag data-testid="rvPoolsChip" kind={pools.kind}>
              {pools.label}
            </Flag>
            <Flag data-testid="rvLateChip" kind={late.kind}>
              {late.label}
            </Flag>
          </Flags>
          <Metrics data-testid="rvMetrics">
            {dependability && (
              <RingWrap data-testid="rvScoreRing">
                <ScoreRing
                  score={dependability.score}
                  size={70}
                  tier={dependability.tier}
                />
              </RingWrap>
            )}
            <MetricBox>
              <MetricLabel>Shifts completed</MetricLabel>
              <MetricValue data-testid="rvShiftsCompleted">
                {dependability ? dependability.stats.shifts_completed : '—'}
              </MetricValue>
            </MetricBox>
            <MetricBox>
              <MetricLabel>On-time rate</MetricLabel>
              <MetricValue data-testid="rvOnTimeRate">
                {onTime == null ? '—' : formatRate(onTime)}
              </MetricValue>
            </MetricBox>
            <MetricBox>
              <MetricLabel>Client rating</MetricLabel>
              <MetricValue data-testid="rvClientRating">
                {rating == null ? '—' : formatRating(rating)}
              </MetricValue>
            </MetricBox>
          </Metrics>
        </Stack>
      </Card.Section>
      <Card.Section>
        <SkillsTitle>Skills</SkillsTitle>
        <SkillsList data-testid="rvSkillsList">
          {skills.length === 0 && <Small>No completed shifts yet</Small>}
          {skills.map((skill) => (
            <SkillItem data-testid="rvSkill" key={skill.id}>
              <SkillIconWrap>
                {skill.image_url && (
                  <img alt={skill.name ?? ''} src={skill.image_url} />
                )}
                <SkillCount>{skill.shifts_count}</SkillCount>
              </SkillIconWrap>
              <SkillName>{skill.name}</SkillName>
            </SkillItem>
          ))}
        </SkillsList>
      </Card.Section>
      <Card.Section>
        {isClientReview(review, source) ? (
          <div data-testid="reviewClientPayload">
            <FactRow>
              <FactLabel>Rating</FactLabel>
              <FactValue>
                {review.rating != null ? `${review.rating}/5` : '—'}
                {review.rated_by ? ` (rated by ${review.rated_by})` : ''}
              </FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Rated at</FactLabel>
              <FactValue>{formatDate(review.rated_at)}</FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Shift</FactLabel>
              <FactValue>{formatDate(review.shift_start_at)}</FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Location</FactLabel>
              <FactValue>{review.account_name ?? '—'}</FactValue>
            </FactRow>
            {review.job_id != null && (
              <FactRow>
                <FactLabel>Job</FactLabel>
                <FactValue>#{review.job_id}</FactValue>
              </FactRow>
            )}
          </div>
        ) : (
          <div data-testid="reviewSystemPayload">
            {(nomination.stale ?? review.stale) != null && (
              <div data-testid="rvStaleBanner" style={{ marginBottom: 12 }}>
                <Alert
                  description="The worker's score dropped below Gold after this suggestion was made. It can still be added or rejected."
                  icon={faExclamationTriangle}
                  status="warning"
                  title="Stale suggestion"
                />
              </div>
            )}
            <FactRow>
              <FactLabel>Qualifying shifts</FactLabel>
              <FactValue>
                {review.qualifying_shift_count ?? '—'}
                {review.current_shift_count != null &&
                  review.qualifying_shift_count != null &&
                  review.current_shift_count !==
                    review.qualifying_shift_count && (
                    <Small data-testid="rvCurrentShifts">
                      {' · now '}
                      {review.current_shift_count}
                    </Small>
                  )}
              </FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Qualifying score</FactLabel>
              <FactValue>
                {review.qualifying_score ?? '—'}
                {review.current_score != null &&
                  review.qualifying_score != null &&
                  review.current_score !== review.qualifying_score && (
                    <Small data-testid="rvCurrentScore">
                      {' · now '}
                      {review.current_score}
                    </Small>
                  )}
              </FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Skill</FactLabel>
              <FactValue>{review.skill_name ?? skillName}</FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Market</FactLabel>
              <FactValue>{review.market_name ?? '—'}</FactValue>
            </FactRow>
          </div>
        )}
        {nomination.also_qualified && (
          <div style={{ marginTop: 8 }}>
            <Small>
              Also qualified via{' '}
              {SOURCE_LABELS[String(nomination.also_qualified.source ?? '')] ??
                'another trigger'}
              .
            </Small>
          </div>
        )}
      </Card.Section>
      <Card.Section>
        {error && (
          <div data-testid="rvError" style={{ marginBottom: 12 }}>
            <Alert
              description={error}
              icon={faExclamationTriangle}
              status="danger"
              title="Decision failed"
            />
          </div>
        )}
        <Stack justify="end">
          <Button
            a11yLabel="Reject nomination"
            appearance="outline"
            data-testid="rvRejectBtn"
            disabled={deciding}
            iconLeft={faTimes}
            label="Reject"
            status="danger"
            onClick={() => onDecline(nomination)}
          />
          <Button
            a11yLabel="Add to pool"
            appearance="outline"
            data-testid="rvAddBtn"
            disabled={deciding}
            iconLeft={faCheck}
            label="Add to pool"
            status="success"
            onClick={() => onApprove(nomination)}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default ReviewNominationModal;
